/**
 * Books of the Bible page — live search, filter tabs, sort dropdown, load-more
 * and clear-filters. Progressive enhancement: initialises only when the expected
 * hooks exist and does nothing if there are no book cards.
 *
 * Hooks (data-* attributes) live in sections/books-of-bible-page.liquid:
 *   root:            [data-books-page]        (carries data-initial-page / data-load-step
 *                                              and the {total}/{shown} result label templates)
 *   search:          [data-books-search], [data-books-search-clear], [data-books-shortcut]
 *   tabs:            [data-books-tab]  inside [data-books-tabs]
 *   sort:            [data-sort-menu], [data-sort-trigger], [data-sort-options],
 *                    [data-sort-option], [data-sort-value-label]
 *   groups:          [data-books-group]  >  [data-books-grid],
 *                    [data-load-more-wrap], [data-load-more], [data-load-bar], [data-load-text]
 *   books:           [data-book]  with data-name / data-chapters / data-category /
 *                                 data-progress / data-order
 *   results:         [data-books-result], [data-books-in-progress], [data-books-clear-filters]
 */
document.addEventListener("DOMContentLoaded", () => {
    const root = document.querySelector("[data-books-page]");
    if (!root) return;

    const search = root.querySelector("[data-books-search]");
    const searchClear = root.querySelector("[data-books-search-clear]");
    const shortcut = root.querySelector("[data-books-shortcut]");
    const tabs = Array.from(root.querySelectorAll("[data-books-tab]"));
    const groups = Array.from(root.querySelectorAll("[data-books-group]"));
    const sortMenu = root.querySelector("[data-sort-menu]");
    const sortTrigger = root.querySelector("[data-sort-trigger]");
    const sortOptions = root.querySelector("[data-sort-options]");
    const sortOptionButtons = Array.from(
        root.querySelectorAll("[data-sort-option]"),
    );
    const sortValueLabel = root.querySelector("[data-sort-value-label]");
    const resultText = root.querySelector("[data-books-result]");
    const clearFilters = root.querySelector("[data-books-clear-filters]");
    let books = Array.from(root.querySelectorAll("[data-book]"));

    if (!books.length) return;

    const initialPage = Number(root.dataset.initialPage) || 15;
    const loadStep = Number(root.dataset.loadStep) || 24;
    const resultAllLabel =
        root.dataset.resultAll || "Showing all [[total]] books";
    const resultFilteredLabel =
        root.dataset.resultFiltered || "Showing [[shown]] of [[total]] books";

    const state = {
        tab: "all",
        query: "",
        sort: "canonical",
        loaded: {},
    };

    groups.forEach((group) => {
        state.loaded[group.dataset.booksGroup] = initialPage;
    });

    const groupBooks = (groupName) =>
        books.filter((book) => book.dataset.category === groupName);

    const visibleGroups = () => {
        switch (state.tab) {
            case "old":
                return ["old"];
            case "new":
                return ["new"];
            case "gospel":
                // Gospel is its own category — not part of Old or New.
                return ["gospel"];
            case "continue":
                return ["old", "new", "gospel"];
            default:
                return ["old", "new", "gospel"];
        }
    };

    const matchesTab = (book) => {
        if (state.tab === "continue") {
            // data-progress is set from localStorage by the block below, so
            // this only reflects the visitor's real, per-device progress.
            const progress = Number(book.dataset.progress) || 0;
            return progress > 0 && progress < 100;
        }
        return true;
    };

    const matchesQuery = (book) => {
        const query = state.query.trim().toLowerCase();
        if (!query) return true;
        const haystack =
            `${book.dataset.name} ${book.dataset.chapters}`.toLowerCase();
        return haystack.includes(query);
    };

    const sorters = {
        canonical: (a, b) => Number(a.dataset.order) - Number(b.dataset.order),
        az: (a, b) => a.dataset.name.localeCompare(b.dataset.name),
        za: (a, b) => b.dataset.name.localeCompare(a.dataset.name),
        chaptersDesc: (a, b) =>
            Number(b.dataset.chapters) - Number(a.dataset.chapters),
        chaptersAsc: (a, b) =>
            Number(a.dataset.chapters) - Number(b.dataset.chapters),
    };

    const setText = (el, template, vars) => {
        if (!el) return;
        el.textContent = Object.entries(vars).reduce(
            (text, [key, value]) => text.replace(`[[${key}]]`, String(value)),
            template,
        );
    };

    const isFiltered = () =>
        state.tab !== "all" ||
        state.query.trim() !== "" ||
        state.sort !== "canonical";

    const apply = (resetPagination = false) => {
        const target = visibleGroups();
        let matchingTotal = 0;

        groups.forEach((group) => {
            const groupName = group.dataset.booksGroup;
            const visible = target.includes(groupName);
            group.style.display = visible ? "flex" : "none";
            if (!visible) return;

            const grid = group.querySelector("[data-books-grid]");
            if (!grid) return;

            const all = groupBooks(groupName);
            const list = all
                .filter((book) => matchesQuery(book) && matchesTab(book))
                .sort(sorters[state.sort] || sorters.canonical);

            matchingTotal += list.length;
            if (resetPagination) state.loaded[groupName] = initialPage;
            const shown = Math.min(state.loaded[groupName], list.length);

            // Hide every book first, then reveal the first `shown` matching books in
            // their sorted order. Hiding all first keeps stale books hidden when the
            // filter/tab/search/sort changes.
            all.forEach((book) => {
                book.hidden = true;
            });
            list.forEach((book, index) => {
                grid.appendChild(book);
                book.hidden = index >= shown;
            });

            const loadWrap = group.querySelector("[data-load-more-wrap]");
            if (loadWrap) {
                const loadBar = loadWrap.querySelector("[data-load-bar]");
                const loadText = loadWrap.querySelector("[data-load-text]");
                const percent = list.length
                    ? Math.round((shown / list.length) * 100)
                    : 0;
                if (loadBar) loadBar.style.width = `${percent}%`;
                if (loadText) {
                    const template =
                        shown >= list.length
                            ? loadWrap.dataset.allLabel ||
                              "All [[total]] books shown"
                            : loadWrap.dataset.partialLabel ||
                              "Showing [[shown]] of [[total]] books";
                    setText(loadText, template, { shown, total: list.length });
                }
                loadWrap.hidden = shown >= list.length;
            }
        });

        // Tab active state
        tabs.forEach((tab) => {
            const active = tab.dataset.booksTab === state.tab;
            tab.dataset.active = String(active);
            tab.setAttribute("aria-selected", String(active));
        });

        // Result row — shows how many books match the current filter (not the
        // paginated count), e.g. "Showing all 65 books" / "Showing 4 of 65 books".
        const filtered = isFiltered();
        setText(resultText, filtered ? resultFilteredLabel : resultAllLabel, {
            shown: matchingTotal,
            total: books.length,
        });

        if (clearFilters) {
            clearFilters.style.display = filtered ? "inline-flex" : "none";
            if (filtered) {
                clearFilters.removeAttribute("aria-hidden");
            } else {
                clearFilters.setAttribute("aria-hidden", "true");
            }
        }

        if (searchClear)
            searchClear.style.display = state.query ? "flex" : "none";
        if (shortcut) shortcut.style.display = state.query ? "none" : "";

        if (sortValueLabel) {
            const activeOption = sortOptionButtons.find(
                (option) => option.dataset.sortOption === state.sort,
            );
            if (activeOption)
                sortValueLabel.textContent = activeOption.dataset.label;
        }

        sortOptionButtons.forEach((option) => {
            const selected = option.dataset.sortOption === state.sort;
            option.dataset.selected = String(selected);
            option.setAttribute("aria-selected", String(selected));
        });
    };

    // ---- Search -------------------------------------------------------------
    if (search) {
        search.addEventListener("input", () => {
            state.query = search.value;
            apply(true);
        });
    }

    if (searchClear) {
        searchClear.addEventListener("click", () => {
            if (!search) return;
            search.value = "";
            state.query = "";
            search.focus();
            apply(true);
        });
    }

    // ⌘K / Ctrl+K focuses the search field
    document.addEventListener("keydown", (event) => {
        if (
            (event.metaKey || event.ctrlKey) &&
            event.key.toLowerCase() === "k"
        ) {
            event.preventDefault();
            if (search) {
                search.focus();
                search.select();
            }
        }
    });

    // ---- Tabs ---------------------------------------------------------------
    tabs.forEach((tab, index) => {
        tab.addEventListener("click", () => {
            if (state.tab === tab.dataset.booksTab) return;
            state.tab = tab.dataset.booksTab;
            apply(true);
        });

        tab.addEventListener("keydown", (event) => {
            if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
            event.preventDefault();
            const direction = event.key === "ArrowRight" ? 1 : -1;
            const next = (index + direction + tabs.length) % tabs.length;
            tabs[next].focus();
            tabs[next].click();
        });
    });

    // ---- Sort dropdown ------------------------------------------------------
    const setMenuOpen = (open) => {
        if (!sortMenu || !sortTrigger) return;
        const isOpen = String(open);
        sortMenu.dataset.open = isOpen;
        sortTrigger.dataset.open = isOpen;
        sortTrigger.setAttribute("aria-expanded", isOpen);
        if (sortOptions) sortOptions.style.display = open ? "flex" : "none";
    };

    if (sortTrigger) {
        sortTrigger.addEventListener("click", () => {
            setMenuOpen(sortMenu.dataset.open !== "true");
        });
    }

    document.addEventListener("click", (event) => {
        if (
            sortMenu &&
            sortMenu.dataset.open === "true" &&
            !sortMenu.contains(event.target)
        ) {
            setMenuOpen(false);
        }
    });

    document.addEventListener("keydown", (event) => {
        if (
            event.key === "Escape" &&
            sortMenu &&
            sortMenu.dataset.open === "true"
        ) {
            setMenuOpen(false);
            if (sortTrigger) sortTrigger.focus();
        }
    });

    sortOptionButtons.forEach((option) => {
        option.addEventListener("click", () => {
            state.sort = option.dataset.sortOption;
            apply(true);
            setMenuOpen(false);
        });
    });

    // ---- Load more ----------------------------------------------------------
    groups.forEach((group) => {
        const loadMore = group.querySelector("[data-load-more]");
        if (!loadMore) return;
        loadMore.addEventListener("click", () => {
            const groupName = group.dataset.booksGroup;
            state.loaded[groupName] += loadStep;
            apply(false);
        });
    });

    // ---- Clear filters ------------------------------------------------------
    if (clearFilters) {
        clearFilters.addEventListener("click", () => {
            state.tab = "all";
            state.query = "";
            state.sort = "canonical";
            if (search) search.value = "";
            apply(true);
        });
    }

    // ---- Full-catalogue augmentation (all 66 books) ---------------------------
    // Liquid only renders the first 50 books. When the Storefront API token is
    // configured, books-catalogue.js provides the full catalogue; render any
    // missing books as tiles so all 66 appear, and refresh the counts.
    const esc = (value) =>
        String(value).replace(
            /[&<>"']/g,
            (ch) =>
                ({
                    "&": "&amp;",
                    "<": "&lt;",
                    ">": "&gt;",
                    '"': "&quot;",
                    "'": "&#39;",
                })[ch],
        );

    const buildTile = (book, chapterSingular, chapterPlural) => {
        const name = String(book.name || "");
        const count =
            book.chapterCount ||
            (Array.isArray(book.chapters) ? book.chapters.length : 0);
        const label = count === 1 ? chapterSingular : chapterPlural;
        const art = book.fallback_art || "plum";
        const initial = String(
            book.fallback_initial || name.charAt(0) || "A",
        ).toUpperCase();
        const cover = book.poster
            ? `<img class="book-tile__cover-img" src="${esc(book.poster)}" alt="${esc(name)}" loading="lazy">`
            : `<span class="book-tile__letter" aria-hidden="true">${esc(initial)}</span>`;

        const el = document.createElement("article");
        el.className = "book-tile-wrap";
        el.setAttribute("data-book", "");
        el.setAttribute("data-name", name.toLowerCase());
        el.setAttribute("data-name-display", name);
        el.setAttribute("data-category", book.category);
        el.setAttribute("data-order", String(book.sort_order || 0));
        el.setAttribute("data-description", book.description || "");
        el.setAttribute("data-chapters", String(count));
        el.setAttribute("data-chapter-singular", chapterSingular);
        el.setAttribute("data-chapter-plural", chapterPlural);
        el.innerHTML = `<a href="/pages/book-details?book=${encodeURIComponent(name)}" class="book-tile no-reveal flex flex-col items-center gap-[1.8rem] pt-[1.4rem]" aria-label="${esc(name)}" data-should-reveal="false">
                <span class="book-tile__cover book-tile__cover--${esc(art)}">
                    ${cover}
                    <span class="book-tile__scrim" data-book-scrim aria-hidden="true" style="display:none"></span>
                    <span class="book-tile__progress" data-book-progress aria-hidden="true" style="display:none"><span class="book-tile__progress-fill block" data-book-progress-fill style="width:0%"></span></span>
                    <span class="book-tile__continue" data-book-continue style="display:none">Continue</span>
                </span>
                <span class="book-tile__meta flex flex-col gap-1 items-center">
                    <span class="book-tile__name font-heading font-medium text-[2rem] leading-[2.4rem] text-ink max-xs:text-[1.7rem] max-xs:leading-[2.1rem]">${esc(name)}</span>
                    <span class="book-tile__chapters text-[1.25rem] text-ink-soft">${count} ${label}</span>
                </span>
            </a>`;
        return el;
    };

    const updateCounts = (catalogue) => {
        const per = { all: catalogue.length, old: 0, new: 0, gospel: 0 };
        const meta = {
            old: { books: 0, chapters: 0 },
            new: { books: 0, chapters: 0 },
            gospel: { books: 0, chapters: 0 },
        };
        catalogue.forEach((b) => {
            const cat =
                b.category === "gospel"
                    ? "gospel"
                    : b.category === "old"
                      ? "old"
                      : "new";
            per[cat] = (per[cat] || 0) + 1;
            meta[cat].books += 1;
            meta[cat].chapters += b.chapterCount || 0;
        });
        Object.entries(per).forEach(([name, n]) => {
            const badge = root.querySelector(
                `[data-books-tab-count="${name}"]`,
            );
            if (badge) badge.textContent = String(n);
        });
        ["old", "new", "gospel"].forEach((name) => {
            const el = root.querySelector(`[data-books-group-meta="${name}"]`);
            if (!el) return;
            const m = meta[name];
            const bLabel = m.books === 1 ? "book" : "books";
            const cLabel = m.chapters === 1 ? "chapter" : "chapters";
            el.textContent = `${m.books} ${bLabel} · ${m.chapters} ${cLabel}`;
        });
    };

    const augmentFromCatalogue = async () => {
        let catalogue = [];
        try {
            catalogue = (await window.ORI.books) || [];
        } catch (error) {
            return;
        }
        if (!catalogue.length) return;

        const existing = new Set(books.map((b) => b.dataset.name));
        const groupGrids = {};
        groups.forEach((group) => {
            groupGrids[group.dataset.booksGroup] =
                group.querySelector("[data-books-grid]");
        });
        const first = books[0];
        const chapterSingular =
            (first && first.dataset.chapterSingular) || "chapter";
        const chapterPlural =
            (first && first.dataset.chapterPlural) || "chapters";

        let added = 0;
        catalogue.forEach((book) => {
            const key = String(book.name || "").toLowerCase();
            if (existing.has(key)) return;
            const grid = groupGrids[book.category];
            if (!grid) return;
            grid.appendChild(buildTile(book, chapterSingular, chapterPlural));
            existing.add(key);
            added++;
        });

        if (!added) return;
        books = Array.from(root.querySelectorAll("[data-book]"));
        updateCounts(catalogue);
    };

    // ---- Apply locally-tracked progress -------------------------------------
    // Progress is tracked per chapter (localStorage, progress.js). The chapter
    // count comes server-side from the Book metaobject; the tile shows the
    // book-level aggregate ("how much of the book is read").
    const applyLocalProgress = () => {
        books.forEach((card) => {
            const name = card.dataset.name || "";
            const count = Number(card.dataset.chapters) || 0;
            const chapters = Array.from({ length: count });
            const agg =
                window.VOBProgress && count
                    ? window.VOBProgress.forBook(name, chapters)
                    : { percent: 0, chaptersCount: count };
            card.dataset.progress = String(agg.percent);
            const fill = card.querySelector("[data-book-progress-fill]");
            if (fill) fill.style.width = `${agg.percent}%`;
            const reveal = agg.percent > 0;
            [
                card.querySelector("[data-book-scrim]"),
                card.querySelector("[data-book-progress]"),
            ].forEach((el) => {
                if (el) el.style.display = reveal ? "" : "none";
            });
            const cont = card.querySelector("[data-book-continue]");
            if (cont)
                cont.style.display = reveal && agg.percent < 100 ? "" : "none";
        });

        // Keep the "Continue watching" tab badge and the "X in progress"
        // summary in sync with the locally-tracked progress.
        const inProgress = books.filter((card) => {
            const p = Number(card.dataset.progress) || 0;
            return p > 0 && p < 100;
        }).length;
        const inProgressEl = root.querySelector("[data-books-in-progress]");
        if (inProgressEl) inProgressEl.textContent = String(inProgress);
        const continueBadge = root.querySelector(
            '[data-books-tab-count="continue"]',
        );
        if (continueBadge) continueBadge.textContent = String(inProgress);

        apply(false);
    };

    // Augment with the full catalogue (all 66 books via Storefront API when a
    // token is set), then apply locally-tracked progress + filters.
    augmentFromCatalogue().then(applyLocalProgress);
});
